<template>
    <page-container>
        <page-header> Outbound Messages </page-header>
        <div class="vx-flex vx-flex-col vx-gap-6 xl:vx-flex-row">
            <div class="vx-shrink-0 xl:vx-w-60">
                <analytics-outbound-messages-sidebar
                    v-model="fetchParams"
                    @reset="resetFilters()"
                ></analytics-outbound-messages-sidebar>
            </div>

            <div class="vx-w-full vx-relative">
                <transition
                    enter-from-class="vx-opacity-0 -vx-translate-y-4"
                    enter-to-class="vx-opacity-100 vx-translate-y-0"
                    leave-from-class="vx-opacity-100 vx-translate-y-0"
                    leave-to-class="vx-opacity-0 -vx-translate-y-4"
                >
                    <div v-if="showLoadingBars" class="vx-absolute vx-w-full vx-transition vx-pt-20">
                        <loading-bars></loading-bars>
                    </div>
                </transition>

                <div
                    class="vx-absolute vx-w-full vx-transition vx-z-[1]"
                    :class="{
                        'vx-opacity-0 vx-pointer-events-none': showLoadingBars || messagesSent,
                    }"
                >
                    <vx-box
                        color="info"
                        class="vx-flex vx-flex-col vx-px-8 vx-py-20 vx-gap-6 vx-items-center vx-text-xl vx-text-slate-500"
                    >
                        <div class="vx-mx-auto vx-text-center">No messages match your current filters.</div>
                        <vx-button type="button" color="primary" size="lg" @click="resetFilters">
                            Reset Filters
                            <font-awesome-icon :icon="faArrowRotateLeft" />
                        </vx-button>
                    </vx-box>
                </div>

                <div
                    v-if="debouncedFetchParams"
                    class="vx-grid vx-gap-6 vx-grid-cols-1 sm:vx-grid-cols-12"
                    :class="{
                        'vx-opacity-0 vx-pointer-events-none': showLoadingBars || !messagesSent,
                        'vx-opacity-100 vx-delay-200 vx-transition vx-duration-150': !(
                            showLoadingBars || !messagesSent
                        ),
                    }"
                >
                    <analytics-outbound-messages-total-active-resources-by-resource-type
                        v-if="showActiveResourcesByResourceType"
                        :fetchParams="debouncedFetchParams"
                        resourceType="user"
                        minHeight="128px"
                        class="sm:vx-col-span-6 lg:vx-col-span-3"
                    />
                    <analytics-outbound-messages-total-active-resources-by-resource-type
                        v-if="showActiveResourcesByResourceType"
                        :fetchParams="debouncedFetchParams"
                        resourceType="campaign"
                        minHeight="128px"
                        class="sm:vx-col-span-6 lg:vx-col-span-3"
                    />
                    <analytics-outbound-messages-total-active-resources-by-resource-type
                        v-if="showActiveResourcesByResourceType"
                        :fetchParams="debouncedFetchParams"
                        resourceType="automation"
                        minHeight="128px"
                        class="sm:vx-col-span-6 lg:vx-col-span-3"
                    />
                    <analytics-outbound-messages-total-active-resources-by-resource-type
                        v-if="showActiveResourcesByResourceType"
                        :fetchParams="debouncedFetchParams"
                        resourceType="playbook"
                        minHeight="128px"
                        class="sm:vx-col-span-6 lg:vx-col-span-3"
                    />

                    <analytics-outbound-messages-total-messages-sent-by-audience-type
                        :fetchParams="debouncedFetchParams"
                        class="vx-col-span-12"
                    />

                    <analytics-outbound-messages-total-contacts-messaged
                        :fetchParams="debouncedFetchParams"
                        class="sm:vx-col-span-6 lg:vx-col-span-3"
                    />
                    <analytics-outbound-messages-total-messages-sent-by-message-type
                        ref="messagesSentChart"
                        :fetchParams="debouncedFetchParams"
                        class="sm:vx-col-span-6 lg:vx-col-span-3"
                    />
                    <analytics-outbound-messages-total-messages-sent-by-message-type
                        :fetchParams="debouncedFetchParams"
                        messageType="sms"
                        class="sm:vx-col-span-6 lg:vx-col-span-3"
                    />
                    <analytics-outbound-messages-total-messages-sent-by-message-type
                        :fetchParams="debouncedFetchParams"
                        messageType="mms"
                        class="sm:vx-col-span-6 lg:vx-col-span-3"
                    />

                    <analytics-outbound-messages-delivery-rate
                        :fetchParams="debouncedFetchParams"
                        class="sm:vx-col-span-6 lg:vx-col-span-3"
                    />
                    <analytics-outbound-messages-click-through-rate
                        :fetchParams="debouncedFetchParams"
                        class="sm:vx-col-span-6 lg:vx-col-span-3"
                    />
                    <analytics-outbound-messages-message-reply-rate
                        :fetchParams="debouncedFetchParams"
                        class="sm:vx-col-span-6 lg:vx-col-span-3"
                    />
                    <analytics-outbound-messages-opt-out-rate
                        :fetchParams="debouncedFetchParams"
                        class="sm:vx-col-span-6 lg:vx-col-span-3"
                    />

                    <template v-if="!fetchParams.filters.resources?.length">
                        <analytics-outbound-messages-total-messages-sent-by-resource
                            @resource:click="setResource"
                            :fetchParams="debouncedFetchParams"
                            class="vx-col-span-6"
                        />
                        <analytics-outbound-messages-rate-type-by-resource
                            @resource:click="setResource"
                            rateType="click-through"
                            :fetchParams="debouncedFetchParams"
                            class="vx-col-span-6"
                        />
                        <analytics-outbound-messages-rate-type-by-resource
                            @resource:click="setResource"
                            rateType="message-reply"
                            :fetchParams="debouncedFetchParams"
                            class="vx-col-span-6"
                        />
                        <analytics-outbound-messages-rate-type-by-resource
                            @resource:click="setResource"
                            rateType="opt-out"
                            :fetchParams="debouncedFetchParams"
                            class="vx-col-span-6"
                        />
                    </template>
                </div>
            </div>
        </div>
    </page-container>
</template>

<script setup>
import PageHeader from '~/components/general/PageHeader.vue';
import PageContainer from '~/components/general/PageContainer.vue';
import { computed, ref, watch } from 'vue';
import { faArrowRotateLeft } from '@fortawesome/pro-solid-svg-icons';
import { VxBox, VxButton } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import LoadingBars from '../general/LoadingBars.vue';
import AnalyticsOutboundMessagesSidebar from './AnalyticsOutboundMessagesSidebar.vue';
import AnalyticsOutboundMessagesTotalActiveResourcesByResourceType from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesTotalActiveResourcesByResourceType.vue';
import AnalyticsOutboundMessagesTotalMessagesSentByAudienceType from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesTotalMessagesSentByAudienceType.vue';
import AnalyticsOutboundMessagesTotalContactsMessaged from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesTotalContactsMessaged.vue';
import AnalyticsOutboundMessagesTotalMessagesSentByMessageType from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesTotalMessagesSentByMessageType.vue';
import AnalyticsOutboundMessagesDeliveryRate from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesDeliveryRate.vue';
import AnalyticsOutboundMessagesClickThroughRate from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesClickThroughRate.vue';
import AnalyticsOutboundMessagesMessageReplyRate from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesMessageReplyRate.vue';
import AnalyticsOutboundMessagesOptOutRate from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesOptOutRate.vue';
import AnalyticsOutboundMessagesRateTypeByResource from './charts/time-series/AnalyticsOutboundMessagesRateTypeByResource.vue';
import AnalyticsOutboundMessagesTotalMessagesSentByResource from './charts/time-series/AnalyticsOutboundMessagesTotalMessagesSentByResource.vue';
import { getDateRange, resourceTypesOptions } from '~/utils/analytics';
import { useQueryParams } from '~/composables/useQueryString';
import { audienceTypes } from '~/constants/campaign';

const defaultFilters = () => {
    const last30Days = getDateRange('last_30_days');
    return {
        range: [last30Days.start_date, last30Days.end_date],
        filters: {
            audience_types: audienceTypes().map((audienceType) => audienceType.name),
            resource_types: resourceTypesOptions.map((resourceType) => resourceType.value),
            resources: undefined,
        },
    };
};

const fetchParams = useQueryParams({
    initialValue: defaultFilters(),
});

const debouncedFetchParams = ref();

/**
 * @param {MouseEvent} event
 */
const setResource = (event, resource) => {
    if (event.metaKey || event.ctrlKey) {
        return;
    }

    event.preventDefault();
    fetchParams.filters.resources = resource
        ? [{ resource_type: resource.resource_type, resource_id: resource.resource_id }]
        : undefined;
};

const showActiveResourcesByResourceType = computed(() => {
    return (
        !(
            fetchParams.filters.resource_types?.length === 1 && fetchParams.filters.resource_types?.includes?.('other')
        ) && !fetchParams.filters.resources?.length
    );
});

const resetFilters = () => {
    Object.assign(fetchParams, defaultFilters());
};

const messagesSentChart = ref();
const messagesSent = computed(() => !messagesSentChart.value?.loading && messagesSentChart.value?.value);
const loading = ref(true);
const showLoadingBars = computed(() => loading.value || messagesSentChart.value?.loading);

let loadingTimeout;
let fetchParamsTimeout;

watch(
    fetchParams,
    () => {
        clearTimeout(loadingTimeout);
        clearTimeout(fetchParamsTimeout);

        const debounceDuration = 600;

        // debounces fetch params to avoid making requests too fast.
        fetchParamsTimeout = setTimeout(() => {
            debouncedFetchParams.value = JSON.parse(JSON.stringify(fetchParams));
        }, debounceDuration);

        // loading is shown for at least 300ms after the params have changed.
        loading.value = true;
        loadingTimeout = setTimeout(() => {
            loading.value = false;
        }, debounceDuration + 300);
    },
    {
        deep: true,
        immediate: true,
    }
);
</script>
